export interface TermDraft {
  amountRupees: string
  dueDate: string
}

/**
 * Splits a `paise` amount into `count` term drafts. The remainder from an uneven
 * division lands on the first term so the rows always sum back to the input (no
 * paise drift). Dates start at `firstDue` and step `stepMonths` calendar months
 * per term (1 = monthly, 3 = quarterly; pass count=1 for a one-time fee).
 */
export function splitIntoTerms(
  paise: number,
  count: number,
  firstDue: string,
  stepMonths = 1,
): TermDraft[] {
  const base = Math.floor(paise / count)
  const remainder = paise - base * count
  const start = new Date(`${firstDue}T00:00:00`)
  return Array.from({ length: count }, (_, i) => {
    const part = base + (i === 0 ? remainder : 0)
    const due = new Date(start)
    due.setMonth(due.getMonth() + i * stepMonths)
    return {
      amountRupees: (part / 100).toString(),
      dueDate: due.toISOString().slice(0, 10),
    }
  })
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Split a `paise` amount into `count` term drafts spread EVENLY between two dates.
 * The amount is divided equally (remainder on the first term, so rows sum back to
 * the input). Dates are evenly spaced with term 1 on `startIso`, the last term on
 * `endIso`, and the rest distributed in between. count=1 → a single term on the
 * start date.
 */
export function splitBetweenDates(
  paise: number,
  count: number,
  startIso: string,
  endIso: string,
): TermDraft[] {
  const base = Math.floor(paise / count)
  const remainder = paise - base * count
  const start = new Date(`${startIso}T00:00:00`)
  const end = new Date(`${endIso}T00:00:00`)
  const spanDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / MS_PER_DAY))
  return Array.from({ length: count }, (_, i) => {
    const part = base + (i === 0 ? remainder : 0)
    // Evenly spaced: i=0 → start, i=count-1 → end.
    const offsetDays = count > 1 ? Math.round((i * spanDays) / (count - 1)) : 0
    const due = new Date(start.getTime() + offsetDays * MS_PER_DAY)
    return {
      amountRupees: (part / 100).toString(),
      dueDate: due.toISOString().slice(0, 10),
    }
  })
}
